// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  CircularProgress,
  LinearProgress,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { omitBy } from 'lodash';
import { useMemo, useState } from 'react';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
// store
import { api as stockApi } from '../store/domains/stock';
import { useDebounce } from '../utils/debounce';
import { ORGANIZATION_TYPES, ROWS_PER_PAGE_OPTIONS } from '../constants/dealer';
import DeleteConfirmDialog from '../components/Dialogs/DeleteConfirmDialog';

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'organizationType', label: 'Organization Type', alignRight: false },
  { id: 'actions', label: '', alignRight: false },
];

export default function DealerTerminate() {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 400);
  const [pinValue, setPinValue] = useState('');
  const debouncedPin = useDebounce(pinValue, 400);
  const [usernameValue, setUsernameValue] = useState('');
  const debouncedUsername = useDebounce(usernameValue, 400);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [organizationType, setOrganizationType] = useState('DEALER');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDealerId, setSelectedDealerId] = useState(null);


  const params = useMemo(
    () =>
      omitBy(
        {
          size: rowsPerPage,
          page: page + 1,
          organizationType,
          title: debouncedSearch,
          pin: debouncedPin,
          userName: debouncedUsername,
        },
        (v) => v == null || v.toString().length < 1
      ),
    [page, rowsPerPage, organizationType, debouncedSearch, debouncedPin, debouncedUsername]
  );

  const { data: dealer, isLoading: dealerLoading, isFetching } = stockApi.useGetDealers(params);

  const [terminateDealer, { isLoading: terminateDealerLoading }] = stockApi.useTerminateDealer();

  const handleChangeSearchInput = (event) => {
    setSearchValue(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDeleteDialog = (dealerId) => {
    setSelectedDealerId(dealerId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedDealerId(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDealerId) return;
    try {
      await terminateDealer({ dealerId: selectedDealerId, organizationType }).unwrap();
    } catch (error) {
      console.error('Failed to terminate dealer', error);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  if (dealerLoading) {
    return <CircularProgress />;
  }

  const rows = dealer?.data?.content ?? [];
  const totalCount = dealer?.data?.totalElements ?? 0;

  return (
    <Page>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <TextField
            label="Title"
            variant="outlined"
            size="small"
            value={searchValue}
            onChange={handleChangeSearchInput}
            placeholder="Enter title"
          />

          <TextField
            label="PIN"
            variant="outlined"
            size="small"
            value={pinValue}
            onChange={(e) => { setPinValue(e.target.value); setPage(0); }}
            placeholder="Enter PIN"
          />
          <TextField
            label="Username"
            variant="outlined"
            size="small"
            value={usernameValue}
            onChange={(e) => { setUsernameValue(e.target.value); setPage(0); }}
            placeholder="Enter username"
          />

          <FormControl>
            <InputLabel id="organization-type-label">Type</InputLabel>
            <Select
              labelId="organization-type-label"
              label="Type"
              value={organizationType}
              onChange={(e) => {
                setOrganizationType(e.target.value);
                setPage(0);
              }}
            >
              {ORGANIZATION_TYPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Card>
          {isFetching && <LinearProgress />}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} rowCount={rows.length} />
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      const { id, title, status } = row;
                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCell padding="checkbox" />
                          <TableCell component="th" scope="row">
                            {id}
                          </TableCell>
                          <TableCell align="left">{title}</TableCell>
                          <TableCell align="left">{status}</TableCell>
                          <TableCell align="left">{organizationType}</TableCell>
                          <TableCell align="center">
                            <button
                              type="button"
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: 'red',
                                cursor: 'pointer',
                              }}
                              onClick={() => handleOpenDeleteDialog(id)}
                              disabled={terminateDealerLoading}
                            >
                              Delete
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length + 1} align="center">
                        No results
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <DeleteConfirmDialog
          loading={terminateDealerLoading}
          open={deleteDialogOpen}
          handleClose={handleCloseDeleteDialog}
          handleConfirmDelete={handleConfirmDelete}
        />
      </Container>
    </Page>
  );
}
